<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import WorkingHoursEditor from './WorkingHoursEditor.vue';

const props = defineProps({
  draft: { type: Object, required: true },
  isAi: { type: Boolean, required: true },
  inboxes: { type: Array, required: true },
  assistants: { type: Array, required: true },
  managers: { type: Array, required: true },
  agents: { type: Array, required: true },
  isSaving: { type: Boolean, default: false },
  inboxDisabled: { type: Function, required: true },
  inboxLabel: { type: Function, required: true },
});

const emit = defineEmits(['save', 'cancel']);

const agentHours = () => {
  const id = String(props.draft.selectedAgentId || '');
  return (
    props.draft.settings.agent_working_hours?.[id] ||
    props.draft.settings.global_working_hours ||
    []
  );
};

const updateAgentHours = hours => {
  const id = String(props.draft.selectedAgentId || '');
  if (!id) return;
  // O objeto é deliberadamente um rascunho mutável, salvo pelo componente pai.
  // eslint-disable-next-line vue/no-mutating-props
  props.draft.settings.agent_working_hours = {
    ...props.draft.settings.agent_working_hours,
    [id]: hours,
  };
};

const resetAgentHours = () => {
  const id = String(props.draft.selectedAgentId || '');
  if (!id) return;
  const schedules = { ...props.draft.settings.agent_working_hours };
  delete schedules[id];
  // eslint-disable-next-line vue/no-mutating-props
  props.draft.settings.agent_working_hours = schedules;
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-mutating-props -->
<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between gap-4">
      <label class="flex flex-1 flex-col gap-1.5">
        <span class="text-body-small font-medium text-n-slate-12"
          >Nome da configuração</span
        >
        <input
          v-model="draft.name"
          class="rounded-md border border-n-strong bg-n-background px-3 py-2 text-n-slate-12"
          placeholder="Ex.: Números comerciais"
        />
      </label>
      <label
        class="mt-6 flex items-center gap-2 text-body-small text-n-slate-12"
      >
        <Switch v-model="draft.enabled" />
        Ativar
      </label>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-body-small font-medium text-n-slate-12"
        >Instâncias</span
      >
      <p v-if="!inboxes.length" class="m-0 text-body-small text-n-slate-11">
        Nenhuma instância compartilhada ou API Oficial configurada está
        disponível.
      </p>
      <div
        v-else
        class="grid gap-2 rounded-md border border-n-strong p-3 sm:grid-cols-2"
      >
        <label
          v-for="inbox in inboxes"
          :key="inbox.id"
          class="flex items-center gap-2 text-body-small text-n-slate-12"
          :class="inboxDisabled(inbox) ? 'opacity-50' : ''"
        >
          <input
            v-model="draft.inbox_ids"
            type="checkbox"
            :value="inbox.id"
            :disabled="inboxDisabled(inbox)"
          />
          {{ inboxLabel(inbox) }}
        </label>
      </div>
    </div>

    <template v-if="isAi">
      <label class="flex flex-col gap-1.5">
        <span class="text-body-small font-medium text-n-slate-12"
          >Agente do Captain</span
        >
        <select
          v-model="draft.captain_assistant_id"
          class="rounded-md border border-n-strong bg-n-background px-3 py-2 text-n-slate-12"
        >
          <option :value="null">Selecione um agente</option>
          <option
            v-for="assistant in assistants"
            :key="assistant.id"
            :value="assistant.id"
          >
            {{ assistant.name }}
          </option>
        </select>
      </label>
      <p class="m-0 text-body-small text-n-slate-11">
        O agente do Captain é único por instância. Ao compartilhar uma
        instância, use o mesmo agente nas configurações que a utilizam.
      </p>
      <WorkingHoursEditor
        v-model="draft.settings.global_working_hours"
        title="Horário da IA"
        helper="A IA somente atende as instâncias deste card dentro deste horário."
      />
    </template>

    <template v-else>
      <div class="grid gap-4 sm:grid-cols-3">
        <label class="flex flex-col gap-1.5">
          <span class="text-body-small font-medium text-n-slate-12"
            >Gestor</span
          >
          <select
            v-model="draft.settings.rodizio_manager_user_id"
            class="rounded-md border border-n-strong bg-n-background px-3 py-2 text-n-slate-12"
          >
            <option :value="null">Selecione um gestor</option>
            <option
              v-for="manager in managers"
              :key="manager.id"
              :value="manager.id"
            >
              {{ manager.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1.5">
          <span class="text-body-small font-medium text-n-slate-12"
            >Entre redistribuições</span
          >
          <input
            v-model.number="draft.settings.rodizio_idle_minutes"
            type="number"
            min="1"
            max="60"
            class="rounded-md border border-n-strong bg-n-background px-3 py-2 text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1.5">
          <span class="text-body-small font-medium text-n-slate-12"
            >Ciclos antes do alerta</span
          >
          <input
            v-model.number="draft.settings.rodizio_max_cycles"
            type="number"
            min="1"
            max="20"
            class="rounded-md border border-n-strong bg-n-background px-3 py-2 text-n-slate-12"
          />
        </label>
      </div>

      <div class="flex items-center gap-5 text-body-small text-n-slate-12">
        <label class="flex items-center gap-2"
          ><input
            v-model="draft.settings.rodizio_horario_mode"
            type="radio"
            value="global"
          />
          Horário geral</label
        >
        <label class="flex items-center gap-2"
          ><input
            v-model="draft.settings.rodizio_horario_mode"
            type="radio"
            value="per_agent"
          />
          Horário por vendedor</label
        >
      </div>
      <WorkingHoursEditor
        v-model="draft.settings.global_working_hours"
        title="Horário do rodízio"
        helper="Este horário é independente do horário da IA."
      />

      <div
        v-if="draft.settings.rodizio_horario_mode === 'per_agent'"
        class="flex flex-col gap-3 rounded-md border border-n-strong p-3"
      >
        <label class="flex flex-col gap-1.5">
          <span class="text-body-small font-medium text-n-slate-12"
            >Vendedor com horário próprio</span
          >
          <select
            v-model="draft.selectedAgentId"
            class="rounded-md border border-n-strong bg-n-background px-3 py-2 text-n-slate-12"
          >
            <option :value="null">Selecione um vendedor</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </label>
        <template v-if="draft.selectedAgentId">
          <WorkingHoursEditor
            :model-value="agentHours()"
            title="Horário do vendedor"
            helper=""
            @update:model-value="updateAgentHours"
          />
          <div class="flex justify-end">
            <Button
              label="Usar horário geral"
              variant="ghost"
              @click="resetAgentHours"
            />
          </div>
        </template>
      </div>

      <div class="flex items-center gap-3 text-body-small text-n-slate-12">
        <Switch v-model="draft.settings.absence_alert_enabled" />
        Alertar possível ausência após
        <input
          v-model.number="draft.settings.absence_alert_delay_minutes"
          type="number"
          min="1"
          max="240"
          class="w-20 rounded-md border border-n-strong bg-n-background px-2 py-1"
        />
        minutos
      </div>
    </template>

    <div class="flex justify-end gap-2">
      <Button label="Cancelar" variant="ghost" @click="emit('cancel')" />
      <Button
        label="Salvar configuração"
        :is-loading="isSaving"
        @click="emit('save')"
      />
    </div>
  </div>
</template>
